import { useEffect, useRef } from 'react';
import { Check, Loader2, X } from 'lucide-react';

interface EditableFieldProps {
  autoFocusAndSelect?: boolean;
  isSaving?: boolean;
  label?: string;
  onCancel?: () => void;
  onChange: (value: string) => void;
  onSave?: ((value: string) => void) | undefined;
  original?: string;
  type?: string;
  value: string;
}

export const EditableField = ({
  autoFocusAndSelect = false,
  isSaving = false,
  label,
  onCancel,
  onChange,
  onSave,
  original = '',
  type = 'text',
  value,
}: EditableFieldProps) => {
  const dirty = onSave !== undefined && value !== original;
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocusAndSelect) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [autoFocusAndSelect]);

  return (
    <div className="grid gap-1">
      {label ? <span className="text-xs font-medium text-ink-subtle">{label}</span> : null}
      <div className="flex items-center gap-1">
        <input
          ref={inputRef}
          className="w-full bg-surface-sunken outline-none min-h-5 rounded-sm text-ink p-2 shadow-sm"
          onChange={(e) => {
            onChange(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onSave?.(value);
            } else if (e.key === 'Escape') {
              onChange(original);
              onCancel?.();
            }
          }}
          type={type}
          value={value}
        />
        {dirty ? (
          <>
            <button
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-success hover:bg-success-tint"
              disabled={isSaving}
              onClick={() => {
                onSave(value);
              }}
              type="button"
            >
              {isSaving ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />}
            </button>
            <button
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-danger hover:bg-danger-tint"
              onClick={() => {
                onChange(original);
              }}
              type="button"
            >
              <X size={14} />
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
};
